import { CheckCircle, XCircle } from '@phosphor-icons/react';
import { differenceInSeconds } from 'date-fns';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { CodeBlock } from './CodeBlock';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useNavigate } from 'react-router-dom';
import { useNotification } from 'src/contexts/NotificationContext';
import { usePlayRecording } from 'src/contexts/PlayRecordingContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { useCampaigns } from 'src/hooks/useCampaigns';
import { Agent } from 'src/interfaces/agent.interface';
import {
  Call,
  getAgentId,
  PostCallResults,
} from 'src/interfaces/call.interface';
import { isCampaignWorkflow } from 'src/services/campaigns/campaigns.helpers';
import type { Campaign } from 'src/services/campaigns/campaigns.interface';
import { friendlyDateFormatter } from 'src/utils/date';
import { processPhoneNumber } from 'src/utils/number';
import { formatSnakeCase } from 'src/utils/string';
import { parseTranscript } from 'src/utils/transcript';
import { BetaTag } from './BetaTag';
import { Button } from './Button';
import { CallErrorDisplay } from './CallErrorDisplay';
import {
  CallRecordingButton,
  CallRecordingButtonRef,
} from './CallRecordingButton';
import { CopyableField } from './CopyableField';
import { Modal } from './Modal';
import {
  isPostCallResultsExtractionMap,
  PostCallResultsRenderer,
} from './PostCallResultsRenderer';
import { Tooltip } from './Tooltip';
import { TranscriptDisplay } from './TranscriptDisplay';
import { HipaaIcon } from './Icons';
import { HipaaCopyEnabled } from 'src/constants';

type CallDetailsModalProps = {
  call: Call;
  isOpen: boolean;
  onClose: () => void;
};

const FLUENTS_CONTEXT_VARIABLE_PREFIX = '$fluents';

const CAMPAIGN_FIELD_KEY = '$ui-only:campaign';

const WORKFLOW_REFERENCE_CONTEXT_KEY =
  '$fluents:workflows:workflow_reference_id';

const MAX_LATENCY_FIELD = 'max_latency';
const AVG_LATENCY_FIELD = 'avg_latency';

const LATENCY_FIELDS = [MAX_LATENCY_FIELD, AVG_LATENCY_FIELD];

const ORDERED_FIELDS = [
  'id',
  'status',
  'start_time',
  'end_time',
  'from_number',
  'to_number',
  'agent',
  CAMPAIGN_FIELD_KEY,
  'human_detection_result',
  'recording_available',
  'telephony_provider',
  'post_call_results',
  'context',
  AVG_LATENCY_FIELD,
  MAX_LATENCY_FIELD,
];

const EXCLUDED_FIELDS = [
  'transcript',
  'telephony_metadata',
  'user_id',
  'action_history',
  'recording_available',
];

const ROW_WIDTH_FIELDS = ['context', 'post_call_results'];

const fieldLabels: Record<string, string> = {
  id: 'ID',
  status: 'Raw Status',
  human_detection_result: 'Human Answered',
  recording_available: 'Recording',
  from_number: 'From Number',
  to_number: 'To Number',
  agent: 'Agent',
  [CAMPAIGN_FIELD_KEY]: 'Campaign',
  telephony_provider: 'Provider',
  start_time: 'Start Time',
  end_time: 'End Time',
  context: 'Context',
};

const stripFluentsContextFields = (
  context: Record<string, unknown>,
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(context).filter(
      ([key]) => !key.startsWith(FLUENTS_CONTEXT_VARIABLE_PREFIX),
    ),
  );

export const CallDetailsModal = ({
  call,
  isOpen,
  onClose,
}: CallDetailsModalProps) => {
  const { get } = useAuthedRequest();
  const campaignsService = useCampaigns();
  const navigate = useNavigate();
  const notification = useNotification();
  const { triggerPlay, audioUrl, audioRef, cleanupAudio } = usePlayRecording();
  const callRecordingButtonRef = useRef<CallRecordingButtonRef>(null);

  const [currentPlayerTime, setCurrentPlayerTime] = useState(0);
  const [agent, setAgent] = useState<Agent | undefined>(undefined);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [campaignLoading, setCampaignLoading] = useState(false);
  const workflowReferenceId = call.context?.[WORKFLOW_REFERENCE_CONTEXT_KEY] as
    | string
    | undefined;

  useEffect(() => {
    const fetchAgent = async () => {
      const agentId = getAgentId(call.agent);
      if (!agentId) {
        setAgent(undefined);
        return;
      }

      try {
        const agent = await get(`/agents/${agentId}`);
        setAgent(agent);
      } catch {
        setAgent(undefined);
      }
    };
    fetchAgent();
  }, [call.agent, get]);

  useEffect(() => {
    if (!workflowReferenceId || !isCampaignWorkflow(workflowReferenceId)) {
      setCampaign(null);
      setCampaignLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setCampaign(null);
        setCampaignLoading(true);
        const fetchedCampaign =
          await campaignsService.fetchCampaign(workflowReferenceId);
        if (!cancelled) {
          setCampaign(fetchedCampaign);
          setCampaignLoading(false);
        }
      } catch (error) {
        console.error(
          `Failed to fetch campaign referenceId=${workflowReferenceId}`,
          error,
        );
        if (!cancelled) {
          setCampaign(null);
          setCampaignLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [campaignsService, workflowReferenceId]);

  const startDate = call.start_time
    ? new Date(Number(call.start_time) * 1000)
    : null;
  const endDate = call.end_time ? new Date(Number(call.end_time) * 1000) : null;
  const durationSecs =
    (startDate && endDate && differenceInSeconds(endDate, startDate)) || 0;

  const downloadTranscript = () => {
    if (!call.transcript) return;

    const element = document.createElement('a');
    const file = new Blob([call.transcript], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'transcript_' + call.id + '.txt';
    document.body.appendChild(element);
    element.click();
  };

  const formatDecimal = (value: number | string, decimals: number = 3) => {
    if (value === null || value === undefined) {
      return value;
    }

    if (typeof value === 'string') {
      value = Number(value);
    }

    if (isNaN(value)) {
      return value;
    }

    return value.toFixed(decimals);
  };

  const getFieldDisplay = (
    fieldKey: string,
    rawValue: unknown,
  ): { label: ReactNode; value: ReactNode } => {
    let label: ReactNode = fieldLabels[fieldKey] || formatSnakeCase(fieldKey);

    // First render UI-only fields.
    if (fieldKey === CAMPAIGN_FIELD_KEY) {
      if (!workflowReferenceId) {
        return { label, value: '-' };
      }

      const campaignName = campaign?.name;
      const isCampaign = isCampaignWorkflow(workflowReferenceId);

      return {
        label,
        value: (
          <div className="flex flex-col gap-1">
            {isCampaign && (
              <span className="text-sm font-medium text-neutral-800">
                {campaignLoading ? (
                  <Skeleton width={160} height={16} />
                ) : campaignName ? (
                  campaignName
                ) : (
                  <span className="text-neutral-500">(deleted)</span>
                )}
              </span>
            )}
            <CopyableField
              value={campaign?.referenceId || workflowReferenceId}
              notification={notification}
            />
          </div>
        ),
      };
    }

    // Now render fields that map to actual values in the call object.
    let displayValue = rawValue;
    if (fieldKey === 'post_call_results') {
      displayValue = (rawValue as PostCallResults | undefined)?.content;
      if (typeof displayValue === 'string' && displayValue.trim() !== '') {
        const stringValue = displayValue as string;
        try {
          const parsed = JSON.parse(stringValue) as unknown;
          if (isPostCallResultsExtractionMap(parsed)) {
            label = 'Insights';
            return {
              label,
              value: <PostCallResultsRenderer results={parsed} />,
            };
          }

          displayValue = parsed;
        } catch {
          return {
            label,
            value: <div className="whitespace-pre-wrap">{stringValue}</div>,
          };
        }
      }
    }

    if (fieldKey === 'context') {
      if (typeof displayValue === 'object' && displayValue !== null) {
        displayValue = stripFluentsContextFields(
          displayValue as Record<string, unknown>,
        );
      }
    }

    if (
      LATENCY_FIELDS.includes(fieldKey as (typeof LATENCY_FIELDS)[number]) &&
      typeof displayValue === 'object' &&
      displayValue !== null &&
      !Array.isArray(displayValue)
    ) {
      const formattedObject = Object.fromEntries(
        Object.entries(displayValue as Record<string, unknown>).map(
          ([key, value]) => {
            if (
              value === null ||
              value === undefined ||
              (typeof value !== 'number' && typeof value !== 'string')
            ) {
              return [key, value];
            }
            return [key, `${formatDecimal(value as number | string)} s`];
          },
        ),
      );
      displayValue = formattedObject;
    }

    if (
      displayValue === null ||
      displayValue === undefined ||
      displayValue === ''
    ) {
      return { label, value: '-' };
    }
    if (Array.isArray(displayValue) && displayValue.length === 0) {
      return { label, value: '-' };
    }
    if (
      typeof displayValue === 'object' &&
      !Array.isArray(displayValue) &&
      Object.keys((displayValue as Record<string, unknown>) || {}).length === 0
    ) {
      return { label, value: '-' };
    }

    if (fieldKey === 'start_time') {
      return { label, value: friendlyDateFormatter(startDate) };
    }

    if (fieldKey === 'end_time') {
      return { label, value: friendlyDateFormatter(endDate) };
    }

    if (fieldKey === 'from_number' || fieldKey === 'to_number') {
      return {
        label,
        value: processPhoneNumber(displayValue?.toString() || ''),
      };
    }

    if (fieldKey === 'human_detection_result') {
      if (durationSecs === 0) {
        return { label, value: '-' };
      } else if (displayValue === 'no_human') {
        return {
          label,
          value: (
            <span className="flex items-center gap-2">
              <XCircle
                size={20}
                weight="fill"
                className="flex-shrink-0 text-error"
              />
              <span style={{ transform: 'translateY(2px)' }}>No</span>
            </span>
          ),
        };
      } else {
        return {
          label,
          value: (
            <span className="flex items-center gap-2 ">
              <CheckCircle
                size={20}
                weight="fill"
                className="flex-shrink-0 text-success"
              />
              <span style={{ transform: 'translateY(2px)' }}>Yes</span>
            </span>
          ),
        };
      }
    }

    if (fieldKey === 'recording_available') {
      return {
        label,
        value: displayValue ? 'Available' : 'Not Available',
      };
    }

    if (fieldKey === 'id') {
      return {
        label,
        value: (
          <CopyableField
            value={displayValue?.toString() || '-'}
            notification={notification}
          />
        ),
      };
    }

    if (fieldKey === 'agent') {
      if (typeof displayValue === 'string') {
        return {
          label,
          value: (
            <CopyableField value={displayValue} notification={notification} />
          ),
        };
      }

      const agentValue = displayValue as { id?: string; name?: string };
      return {
        label,
        value: (
          <CopyableField value={agentValue.id} notification={notification} />
        ),
      };
    }

    if (fieldKey === 'telephony_id') {
      return {
        label,
        value: (
          <CopyableField
            value={
              call.telephony_metadata?.call_sid || call.telephony_id || '-'
            }
            notification={notification}
          />
        ),
      };
    }

    if (fieldKey === 'errors') {
      return {
        label,
        value: (
          <div className="max-h-40 overflow-y-auto">
            <CallErrorDisplay
              errors={displayValue as string[]}
              showWarning={false}
            />
          </div>
        ),
      };
    }

    if (typeof displayValue === 'object') {
      return {
        label,
        value: (
          <table className="w-full border border-neutral-100 bg-neutral-0 table-fixed">
            <tbody>
              {Object.entries(displayValue).map(([key, value]) => (
                <tr key={key}>
                  <td className="py-2 px-4 border-b border-neutral-100 font-medium w-[30%] whitespace-nowrap">
                    {key}
                  </td>
                  <td className="py-2 px-4 border-b border-neutral-100 w-[70%] break-words text-right">
                    {typeof value === 'object' && value !== null ? (
                      <CodeBlock
                        text={JSON.stringify(value, null, 2)}
                        language="json"
                        wrapLongLines={false}
                        showLineNumbers={false}
                      />
                    ) : (
                      value
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ),
      };
    }

    return { label, value: displayValue?.toString() || '-' };
  };

  const isRowWidthField = (field: string | undefined) =>
    !!field && ROW_WIDTH_FIELDS.includes(field);

  const DetailsFullRow = ({ fieldKey }: { fieldKey: string }) => {
    const { label, value } = getFieldDisplay(
      fieldKey,
      (call as Record<string, unknown>)[fieldKey],
    );
    return (
      <tr>
        <td className="py-2 px-4 border-b border-neutral-100 font-medium w-[20%]">
          {label}
        </td>
        <td
          className="py-2 px-4 border-b border-neutral-100 w-[80%]"
          colSpan={3}
        >
          {value}
        </td>
      </tr>
    );
  };

  const DetailsStandardRow = ({
    firstKey,
    secondKey,
  }: {
    firstKey: string;
    secondKey?: string;
  }) => {
    const firstField = getFieldDisplay(
      firstKey,
      (call as Record<string, unknown>)[firstKey],
    );
    const secondField = secondKey
      ? getFieldDisplay(secondKey, (call as Record<string, unknown>)[secondKey])
      : null;

    return (
      <tr>
        <td className="py-2 px-4 border-b border-neutral-100 font-medium w-[20%]">
          {firstField.label}
        </td>
        <td className="py-2 px-4 border-b border-neutral-100 w-[30%] break-all">
          {firstField.value}
        </td>

        {secondField ? (
          <>
            <td className="py-2 px-4 border-b border-neutral-100 font-medium w-[20%]">
              {secondField.label}
            </td>
            <td className="py-2 px-4 border-b border-neutral-100 w-[30%]">
              {secondField.value}
            </td>
          </>
        ) : (
          <>
            <td className="py-2 px-4 border-b border-neutral-100 w-[20%]"></td>
            <td className="py-2 px-4 border-b border-neutral-100 w-[30%]"></td>
          </>
        )}
      </tr>
    );
  };

  const getTelephonyIdRow = () => {
    if (!call.telephony_provider) return null;

    const label = `${formatSnakeCase(call.telephony_provider)} ID`;
    return (
      <tr key="telephony_id">
        <td className="py-2 px-4 border-b border-neutral-100 font-medium w-[20%]">
          {label}
        </td>
        <td
          className="py-2 px-4 border-b border-neutral-100 break-all text-ellipsis whitespace-nowrap overflow-hidden"
          colSpan={3}
        >
          <CopyableField
            value={
              call.telephony_metadata?.call_sid || call.telephony_id || '-'
            }
            notification={notification}
          />
        </td>
      </tr>
    );
  };

  const remainingFields = call
    ? Object.keys(call).filter(
        (key) =>
          !ORDERED_FIELDS.includes(key) &&
          !EXCLUDED_FIELDS.includes(key) &&
          call[key as keyof Call] !== null &&
          call[key as keyof Call] !== undefined &&
          call[key as keyof Call] !== '',
      )
    : [];

  const displayFields = [...ORDERED_FIELDS, ...remainingFields];
  const transcriptMessages = parseTranscript(call.transcript || '');

  const parseTimestampToSeconds = (timestamp: string): number => {
    const parts = timestamp.split(':').map((p) => p.trim());
    let hours = 0;
    let minutes = 0;
    let seconds = 0;

    if (parts.length === 3) {
      hours = Number(parts[0]);
      minutes = Number(parts[1]);
      seconds = Number(parts[2]);
    } else if (parts.length === 2) {
      minutes = Number(parts[0]);
      seconds = Number(parts[1]);
    } else if (parts.length === 1) {
      seconds = Number(parts[0]);
    }

    hours = isNaN(hours) ? 0 : hours;
    minutes = isNaN(minutes) ? 0 : minutes;
    seconds = isNaN(seconds) ? 0 : seconds;

    return hours * 3600 + minutes * 60 + seconds;
  };

  const handleMessageClick = async (messageIndex: number) => {
    let targetTime = 0;

    if (messageIndex === 0) {
      targetTime = 0;
    } else {
      const prevMessage = transcriptMessages[messageIndex - 1];

      if (prevMessage) {
        targetTime = parseTimestampToSeconds(prevMessage.timestamp || '0:00');
      }
    }

    triggerPlay(targetTime);

    if (!audioUrl) {
      callRecordingButtonRef.current?.click();
    }
  };

  const handleClose = () => {
    onClose();
    audioRef.current?.pause();
    cleanupAudio();
  };

  const timestampToSeconds = (timestamp: string) => {
    const parts = timestamp
      .split(':')
      .map((p) => p.trim())
      .reverse();
    const seconds = Number(parts[0]) || 0;
    const minutes = Number(parts[1]) || 0;
    const hours = Number(parts[2]) || 0;
    return hours * 3600 + minutes * 60 + seconds;
  };

  const currentTranscriptMessageIndex = useMemo(() => {
    if (transcriptMessages.length === 0) return -1;

    const messagesWithRanges = transcriptMessages.map((msg, index) => {
      const endTime = timestampToSeconds(msg.timestamp || '0:00');
      const startTime =
        index === 0
          ? 0
          : timestampToSeconds(
              transcriptMessages[index - 1].timestamp || '0:00',
            );

      return {
        ...msg,
        startTime,
        endTime,
      };
    });
    const index = messagesWithRanges.findIndex(
      ({ startTime, endTime }) =>
        currentPlayerTime >= startTime && currentPlayerTime < endTime,
    );

    return index !== -1 ? index : transcriptMessages.length - 1;
  }, [currentPlayerTime, transcriptMessages]);

  useEffect(() => {
    if (currentTranscriptMessageIndex < 0) {
      return;
    }

    const messageElement = document.getElementById(
      `message-${currentTranscriptMessageIndex}`,
    );
    if (messageElement) {
      messageElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [currentTranscriptMessageIndex]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Call Overview"
      className="lg:max-w-[90%] max-w-7xl"
    >
      <div className="flex flex-col lg:flex-row gap-6 h-[78vh]">
        <div className="flex-1 flex flex-col">
          {call.transcript && (
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Transcript</h3>
              <div className="flex gap-2">
                <Tooltip
                  content="Only available for OpenAI agents with human interaction"
                  zIndex={50}
                  position="bottom"
                >
                  <Button
                    onClick={() =>
                      navigate(`/calls/${call.id}/conversation-editor`)
                    }
                    size="small"
                    color="secondary"
                    className="gap-2"
                    disabled={
                      !call.transcript ||
                      parseTranscript(call.transcript).length <= 1 ||
                      (agent?.provider && agent.provider !== 'openai')
                    }
                  >
                    Edit Conversation
                    <BetaTag size="small" />
                  </Button>
                </Tooltip>
                <Button onClick={downloadTranscript} size="small">
                  Download
                </Button>
              </div>
            </div>
          )}

          <TranscriptDisplay
            messages={transcriptMessages}
            onMessageClick={handleMessageClick}
            shouldHighlightMessage={(index) =>
              index === currentTranscriptMessageIndex && currentPlayerTime > 0
            }
            rawTranscript={call.transcript}
            hipaaCompliant={call.hipaa_compliant}
          />

          <div className="flex justify-center mt-4">
            {call.recording_available ? (
              <div className="flex items-center justify-center gap-2">
                {call.hipaa_compliant && (
                  <Tooltip content={HipaaCopyEnabled} position="top">
                    <HipaaIcon
                      className="w-6 h-6 ml-1 text-primary-500"
                      aria-label="HIPAA"
                    />
                  </Tooltip>
                )}
                {!call.hipaa_compliant && (
                  <CallRecordingButton
                    ref={callRecordingButtonRef}
                    callId={call.id}
                    recordingAvailable={call.recording_available}
                    setCurrentTime={setCurrentPlayerTime}
                    showTimeline
                  />
                )}
              </div>
            ) : call.hipaa_compliant ? (
              <Tooltip content={HipaaCopyEnabled} position="top">
                <HipaaIcon
                  className="w-6 h-6 ml-1 text-primary-500"
                  aria-label="HIPAA"
                />
              </Tooltip>
            ) : (
              <span className="text-neutral-400">No recording available.</span>
            )}
          </div>
        </div>

        <div className="flex-1 flex flex-col">
          <h3 className="text-lg font-semibold mb-4">Details</h3>
          <div className="flex-1 overflow-y-auto">
            <table className="w-full border border-neutral-100 bg-neutral-0 table-fixed">
              <tbody>
                {(() => {
                  const validFields = displayFields.filter((key) => {
                    if (EXCLUDED_FIELDS.includes(key)) return false;
                    if (key === CAMPAIGN_FIELD_KEY) {
                      return Boolean(
                        workflowReferenceId &&
                          isCampaignWorkflow(workflowReferenceId),
                      );
                    }

                    const value = call[key as keyof Call];
                    if (value === null || value === undefined || value === '')
                      return false;
                    if (Array.isArray(value) && value.length === 0)
                      return false;
                    if (
                      typeof value === 'object' &&
                      !Array.isArray(value) &&
                      value !== null &&
                      Object.keys(value as Record<string, unknown>).length === 0
                    )
                      return false;

                    return true;
                  });

                  const rows: JSX.Element[] = [];

                  let i = 0;
                  while (i < validFields.length) {
                    const firstKey = validFields[i];
                    const secondKey = validFields[i + 1];

                    // If the first key is a full-width field, let it span the full row
                    if (isRowWidthField(firstKey)) {
                      rows.push(
                        <DetailsFullRow key={firstKey} fieldKey={firstKey} />,
                      );
                      i += 1;
                      continue;
                    }

                    // If the second key is a full-width field, render the first as a normal half-row
                    // and then render the full-width field as its own row
                    if (isRowWidthField(secondKey)) {
                      rows.push(
                        <DetailsStandardRow
                          key={firstKey}
                          firstKey={firstKey}
                        />,
                      );

                      rows.push(
                        <DetailsFullRow
                          key={secondKey}
                          fieldKey={secondKey as string}
                        />,
                      );

                      i += 2;
                      continue;
                    }

                    rows.push(
                      <DetailsStandardRow
                        key={firstKey}
                        firstKey={firstKey}
                        secondKey={secondKey}
                      />,
                    );

                    i += 2;
                  }

                  return rows;
                })()}

                {getTelephonyIdRow()}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Modal>
  );
};
